import React, { Grid, Paper, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { PartnerService } from 'services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAccountInformationForm, useProfilePlaceholder } from 'hooks';
import {
	FormTextInput,
	ErrorHandler,
	StatusIndicator,
	ImageUploader,
	SubmitButton
} from 'components';
import { AccountInformationFormProps } from './AccountInformationFormProps';
import { PartnerPlacement } from 'enums';

export const AccountInformationForm = ({ query }: AccountInformationFormProps) => {
	const { schema } = useAccountInformationForm();
	type FormFields = z.infer<typeof schema>;
	const defaultImage = useProfilePlaceholder();
	const queryClient = useQueryClient();
	const methods = useForm<FormFields>({
		defaultValues: {
			email: query.data?.email ?? '',
			firstName: query.data?.firstName ?? '',
			lastName: query.data?.lastName ?? '',
			addressLine: query.data?.addressLine ?? '',
			imageUrl: query.data?.imageUrl ?? defaultImage,
			bankAccountNumber: query.data?.bankAccountNumber ?? '',
			city: query.data?.city ?? '',
			zipCode: query.data?.zipCode ?? '',
			country: query.data?.country ?? '',
			emailConfirmed: query.data?.emailConfirmed ?? false,
			placementPartnerId: query.data?.placementPartnerId ?? 0,
			mentorId: query.data?.mentorId ?? 0
		},
		resolver: zodResolver(schema),
		mode: 'onBlur',
	});
	const onSubmit = async (data: FormFields) => {
		try {
			await PartnerService.update({
				...data,
				placement: query.data?.placement ?? PartnerPlacement.Left,
			});
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			methods.setError('root', { type: 'manual', message: e.message });
		}
	};
	const onError = async (data: FieldErrors<FormFields>) => {
		console.log(data);
	};
	useMutation({
		mutationFn: onSubmit,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['accountInformation'] });
		},
	});
	useEffect(() => {
		if (query.data) {
			methods.setValue('id', query.data.id);
			methods.setValue('addressLine', query.data.addressLine);
			methods.setValue('city', query.data.city);
			methods.setValue('zipCode', query.data.zipCode);
			methods.setValue('country', query.data.country);
			methods.setValue('email', query.data.email);
			methods.setValue('phoneNumber', query.data.phoneNumber);
			methods.setValue('firstName', query.data.firstName);
			methods.setValue('lastName', query.data.lastName);
			methods.setValue('imageUrl', query.data.imageUrl);
			methods.setValue('bankAccountNumber', query.data.bankAccountNumber);
		}
	}, [methods, query?.data])
	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(onSubmit, onError)} style={{ display: 'flex', justifyContent: 'center', height:'calc(100vh - 64px)', paddingTop:'64px' }}>
				<Grid container justifyContent='center' spacing={2} component={Paper} p={2} maxWidth={1000}>
					{query.isPending && (
						<Grid item xs={12} textAlign={'center'}>
							<CircularProgress />
						</Grid>
					)}
					{query.isError && (
						<Grid item xs={12}>
							<ErrorHandler message={query.error?.message} />
						</Grid>
					)}
					{query.data && (
						<>
							<Grid item xs={12} textAlign={'center'}>
								<ImageUploader base64Image={methods.watch('imageUrl')} />
							</Grid>
							<Grid item xs={6}>
								<FormTextInput name='firstName' />
							</Grid>
							<Grid item xs={6}>
								<FormTextInput name='lastName' />
							</Grid>
							<Grid item xs={11}>
								<FormTextInput name='email' />
							</Grid>
							<Grid item xs={1}>
								<StatusIndicator status={query.data.emailConfirmed} />
							</Grid>
							<Grid item xs={12}>
								<FormTextInput name='phoneNumber' />
							</Grid>
							<Grid item xs={12}>
								<FormTextInput name='bankAccountNumber' />
							</Grid>
							<Grid item xs={12}>
								<FormTextInput name='addressLine' />
							</Grid>
							<Grid item xs={6}>
								<FormTextInput name='zipCode' />
							</Grid>
							<Grid item xs={6}>
								<FormTextInput name='city' />
							</Grid>
							<Grid item xs={12}>
								<FormTextInput name='country' />
							</Grid>
							<Grid item xs={4} textAlign={'center'}>
								<SubmitButton text='Save' />
							</Grid>
						</>
					)}
				</Grid>
			</form>
		</FormProvider>
	);
};